import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

const LOC_LABEL = { subject: "Sujet", message: "Message", priority: "Priorité", deadline: "Échéance", execution_date: "Date d'exécution", assignees: "Assignés", reminders: "Rappels", recurrence: "Récurrence", checklist: "Checklist", project_id: "Projet", email: "Email", telephone: "Téléphone", nom: "Nom", prenom: "Prénom" };
const locOf = (loc) => (Array.isArray(loc) ? loc.filter((x) => x !== "body" && x !== "query" && x !== "path").map((x) => LOC_LABEL[x] || x).join(" › ") : "");

/** Convertit N'IMPORTE QUELLE erreur (axios, string, objet, tableau `detail` 422 FastAPI) en texte affichable. Ne renvoie jamais un objet. */
export function errText(err, fallback = "Une erreur est survenue") {
  const toText = (d) => {
    if (d == null || d === "") return "";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map(toText).filter(Boolean).join(" · ");
    if (typeof d === "object") {
      if (d.msg) { const l = locOf(d.loc); return l ? `${l} : ${d.msg}` : String(d.msg); }
      if (d.message) return toText(d.message);
      if (d.detail) return toText(d.detail);
      try { return JSON.stringify(d); } catch { return String(d); }
    }
    return String(d);
  };
  const data = err?.response?.data;
  return toText(data?.detail ?? data) || toText(err?.message) || fallback;
}

// iter369 pt.3 — anti-doublon des listes : les GET identiques simultanés partagent une requête, une réponse est réutilisée 1,5 s ;
// toute requête d'écriture (POST/PATCH/PUT/DELETE) vide ce cache → la liste se met à jour juste après une action.
const _inflight = new Map();
const _cache = new Map();
const DEDUPE_TTL_MS = 1500;
export function dedupedGet(url, config = {}) {
  const { signal, ...rest } = config;
  const key = `${url}|${JSON.stringify(rest.params || {})}`;
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.at < DEDUPE_TTL_MS) return Promise.resolve(hit.res);
  if (_inflight.has(key)) return _inflight.get(key);
  const p = api.get(url, rest).then((res) => { _cache.set(key, { at: Date.now(), res }); _inflight.delete(key); return res; }, (err) => { _inflight.delete(key); throw err; });
  _inflight.set(key, p);
  return p;
}
export const clearGetCache = () => _cache.clear();

/** iter394 pt 1 — GET avec 2 nouvelles tentatives (réseau / 5xx / délai), annulable (`signal`), sans cache. Jamais de reprise après annulation. */
export async function getWithRetry(url, { signal, retries = 2, timeout = 10000, ...rest } = {}) {
  let last;
  for (let i = 0; i <= retries; i += 1) {
    if (signal?.aborted) throw Object.assign(new Error("canceled"), { code: "ERR_CANCELED" });
    try { return await api.get(url, { ...rest, signal, timeout }); } catch (err) {
      last = err;
      const st = err?.response?.status;
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || (st && st < 500)) throw err;
      if (i < retries) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
    }
  }
  throw last;
}

api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("ledesk_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if ((config.method || "get").toLowerCase() !== "get") _cache.clear();
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Broadcast a "user is active" signal so the LockContext's idle timer
    // resets on every successful API round-trip (spec §c2 — network
    // activity counts as activity, alongside keyboard / mouse / scroll).
    // Skip the noisy /lock-pin/verify endpoint to avoid resetting the
    // timer during the unlock attempt itself.
    if (!/\/lock-pin\/verify$/.test(r.config?.url || "")) {
      try { window.dispatchEvent(new CustomEvent("desk:api-activity")); } catch {}
    }
    if (r.config?.__dbRetry) { try { window.dispatchEvent(new CustomEvent("ledesk:db-outage", { detail: { on: false } })); } catch {} }
    // Spec iter227 (2026-02-28) — Toute mutation d'un document
    // (status change, action, etc.) émet `ledesk:doc-mutated` pour
    // rafraîchir la liste Documents (tri "Activité récente" +
    // colonne Statut). Corrige le bug rapporté après annulation
    // de DC0210/26 : le doc restait « En cours » dans la liste
    // + ne remontait pas en tête. On généralise à TOUTES les URL
    // qui mutent un doc : /documents/{id}/... via POST/PUT/PATCH/DELETE.
    try {
      const method = (r.config?.method || "").toLowerCase();
      const url = r.config?.url || "";
      const mutatingMethods = ["post", "put", "patch", "delete"];
      const docMatch = /\/documents\/([^/?#]+)(?:\/|$)/.exec(url);
      if (mutatingMethods.includes(method) && docMatch) {
        const docId = docMatch[1];
        window.dispatchEvent(new CustomEvent("ledesk:doc-mutated", {
          detail: { docId, reason: "api-mutation", url, method },
        }));
        try { localStorage.setItem("ledesk:doc-mutated", String(Date.now())); } catch {}
      }
    } catch {}
    return r;
  },
  async (err) => {
    // iter371 — coupure base : les lectures (GET) sont réessayées 3 fois (1 s, 2 s, 4 s) ; bandeau « nouvel essai… » pendant ce temps.
    const cfg = err?.config || {};
    const dbDown = (!err.response && err.code !== "ERR_CANCELED") || (err.response?.status === 503 && err.response?.data?.code === "db_unavailable");
    if (dbDown && (cfg.method || "get").toLowerCase() === "get") {
      const n = cfg.__dbRetry || 0;
      if (n < 3) {
        cfg.__dbRetry = n + 1;
        try { window.dispatchEvent(new CustomEvent("ledesk:db-outage", { detail: { on: true, attempt: n + 1 } })); } catch {}
        await new Promise((r) => setTimeout(r, [1000, 2000, 4000][n]));
        return api(cfg);
      }
    }
    // Normalisation : `detail` devient TOUJOURS une chaîne (un tableau 422 Pydantic rendu dans un toast/JSX provoquait l'écran blanc React #31).
    if (err?.response?.data && typeof err.response.data === "object" && err.response.data.detail != null && typeof err.response.data.detail !== "string") {
      err.response.data.detail_raw = err.response.data.detail;
      err.response.data.detail = errText(err);
    }
    if (err.response?.status === 401) {
      const path = window.location.pathname;
      if (path !== "/connexion" && path !== "/activation" && path !== "/") {
        sessionStorage.removeItem("ledesk_token");
        window.location.href = "/connexion";
      }
    }
    return Promise.reject(err);
  },
);

export default api;
